import React from 'react'
import { Card, CardContent, Grid, TextField, MenuItem, FormControlLabel, Checkbox, ToggleButtonGroup, ToggleButton, Typography } from '@mui/material'
import MDBox from 'components/MDBox'
import MDButton from 'components/MDButton'
import MDTypography from 'components/MDTypography'
import { createEmptyAddress } from '../CustomerOnboardingWizard'
import GooglePlacesAutocomplete, { geocodeByPlaceId } from 'react-google-places-autocomplete'

function AddressesStep({ addresses, setAddresses, dropdowns, errors }) {
  const addressTypes = dropdowns.addressTypes || []
  const statusOptions = dropdowns.entityStatusOptions || []

  const updateAddress = (id, field, value) => {
    setAddresses((prev) =>
      prev.map((addr) => (addr.id === id ? { ...addr, [field]: value } : addr)),
    )
  }

  const addAddress = () => {
    setAddresses((prev) => [...prev, createEmptyAddress()])
  }

  const removeAddress = (id) => {
    setAddresses((prev) => (prev.length > 1 ? prev.filter((a) => a.id !== id) : prev))
  }

  const segmentedStyle = {
    height: 44,
    borderRadius: 2,
    backgroundColor: "#f4f4f4",
    "& .MuiToggleButton-root": {
      textTransform: "none",
      fontWeight: 500,
      border: "none",
      borderRadius: 2,
      px: 2,
      "&.Mui-selected": {
        backgroundColor: "#49a3f1 !important",
        color: "#fff !important",
        transition: "all 0.2s ease",
      },
    },
  }

  const handleSelect = async (value, id) => {
    try {
      const results = await geocodeByPlaceId(value.place_id);
      if (results && results.length > 0) {
        const placeData = results[0];
        const { address_components, formatted_address, geometry } = placeData;

        // Create a new address object with selected place information
        const newAddressObj = {
          street_address1: `${address_components.find(comp => comp.types.includes('street_number'))?.long_name || ""} ${address_components.find(comp => comp.types.includes('route'))?.long_name || ""}`,
          street_address2: address_components.find(comp => comp.types.includes('neighborhood'))?.long_name || "",
          city: address_components.find(comp => comp.types.includes('locality'))?.long_name || "",
          state: address_components.find(comp => comp.types.includes('administrative_area_level_1'))?.short_name || "",
          postal_code: address_components.find(comp => comp.types.includes('postal_code'))?.long_name || "",
          country: address_components.find(comp => comp.types.includes('country'))?.long_name || "",
          latitude: geometry?.location.lat() || "",
          longitude: geometry?.location.lng() || "",
        };
        setAddresses((prev) =>
          prev.map((addr) => (addr.id === id ? { ...addr, ...newAddressObj } : addr)),
        )
      }
    } catch (error) {
      console.error('Error fetching geolocation:', error);
    }
  };

  return (
    <MDBox mt={2}>
      {addresses.map((addr, idx) => {
        const addrErrors = errors?.[idx] || {};
        return (
          <Card key={addr.id} variant="outlined" sx={{ mb: 2 }}>
            <CardContent>
              <MDBox display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                <MDTypography variant="subtitle2" fontWeight="medium">
                  Address #{idx + 1}
                </MDTypography>
                <MDButton
                  variant="text"
                  color="error"
                  size="small"
                  disabled={addresses.length === 1}
                  onClick={() => removeAddress(addr.id)}
                >
                  Remove
                </MDButton>
              </MDBox>

              <Grid container spacing={2}>

                <Grid item xs={12} md={12}>
                  <div className="places-autocomplete-container">
                    <GooglePlacesAutocomplete
                      apiKey={process.env.REACT_APP_MAP_API_KEY}
                      onSelect={(value) => handleSelect(value, addr.id)}
                    />

                    <style>{`
                              .places-autocomplete-container {
                                position: relative;
                              }

                              .google-places-autocomplete__input {
                                width: 100%;
                                padding: 8px;
                                border: 1px solid #ccc;
                                border-radius: 4px;
                              }

                              .google-places-autocomplete__suggestions-container {
                                position: absolute;
                                top: calc(100% + 5px);
                                left: 0;
                                width: 100%;
                                background-color: #fff;
                                border: 1px solid #ccc;
                                border-radius: 4px;
                                box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
                                z-index: 1000;
                                max-height: 200px;
                                overflow-y: auto;
                              }

                              .google-places-autocomplete__suggestion {
                                padding: 8px 16px;
                                cursor: pointer;
                              }

                              .google-places-autocomplete__suggestion:hover {
                                background-color: #f0f0f0;
                              }

                              .google-places-autocomplete__suggestion--active {
                                background-color: #e0e0e0;
                              }
                        `}</style>
                  </div>
                </Grid>

                <Grid item xs={12} md={4}>
                  <TextField
                    fullWidth
                    label="Name"
                    value={addr.name}
                    onChange={(e) => updateAddress(addr.id, 'name', e.target.value)}
                    error={Boolean(addrErrors.name)}
                    helperText={addrErrors.name}
                    size="small"
                    InputProps={{
                      sx: { height: 40, minHeight: 40 }
                    }}
                    sx={{
                      '& .MuiInputBase-input': {
                        height: 40,
                        minHeight: 40,
                        boxSizing: 'border-box',
                        display: "flex",
                        alignItems: "center",
                        padding: '16.5px 14px'
                      }
                    }}
                  />
                </Grid>

                <Grid item xs={12} md={4}>
                  <TextField
                    select
                    fullWidth
                    label="Address Type"
                    value={addr.address_type_id}
                    onChange={(e) => updateAddress(addr.id, 'address_type_id', e.target.value)}
                    error={Boolean(addrErrors.address_type_id)}
                    helperText={addrErrors.address_type_id}
                    size="small"
                    InputProps={{
                      sx: { height: 40, minHeight: 40 }
                    }}
                    SelectProps={{
                      MenuProps: { PaperProps: { sx: { maxHeight: 300 } } }
                    }}
                    sx={{
                      '& .MuiSelect-select': { minHeight: 40, display: "flex", alignItems: "center" }
                    }}
                  >
                    {addressTypes.map((type) => (
                      <MenuItem key={type.address_type_id} value={type.address_type_id}>
                        {type.type_name}
                      </MenuItem>
                    ))}
                  </TextField>
                </Grid>

                {/* <Grid item xs={12} md={4}>
                  <TextField
                    select
                    fullWidth
                    label="Status"
                    value={addr.status_type_id}
                    onChange={(e) => updateAddress(addr.id, 'status_type_id', e.target.value)}
                    size="small"
                    InputProps={{
                      sx: { height: 40, minHeight: 40 }
                    }}
                    SelectProps={{
                      MenuProps: { PaperProps: { sx: { maxHeight: 300 } } }
                    }}
                    sx={{
                      '& .MuiSelect-select': { minHeight: 40, display: "flex", alignItems: "center" }
                    }}
                  >
                    {statusOptions.map((s) => (
                      <MenuItem
                        key={s.status_option_id}
                        value={s.status_type_id}
                      >
                        {s.StatusType.status_name}
                      </MenuItem>
                    ))}
                  </TextField>
                </Grid> */}

                <Grid item xs={12} md={4}>
                  {/* <MDTypography variant="caption" fontWeight="bold" sx={{ mb: 1, display: "block" }}>
                    Status
                  </MDTypography> */}

                  <ToggleButtonGroup
                    fullWidth
                    exclusive
                    value={addr.status_type_id}
                    onChange={(e, value) =>
                      value && updateAddress(addr.id, 'status_type_id', value)
                    }
                    sx={segmentedStyle}
                  >
                    {statusOptions.map((s) => {
                      const label = s.StatusType.status_name

                      return (
                        <ToggleButton
                          key={s.status_option_id}
                          value={s.status_type_id}
                          sx={{
                            textTransform: "none",
                            fontWeight: 500,
                          }}
                        >
                          {label}
                        </ToggleButton>
                      )
                    })}
                  </ToggleButtonGroup>

                  {errors?.status_type_id && (
                    <Typography variant="caption" color="error">
                      {errors.status_type_id}
                    </Typography>
                  )}
                </Grid>

                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label="Street Address 1"
                    value={addr.street_address1}
                    onChange={(e) => updateAddress(addr.id, 'street_address1', e.target.value)}
                    error={Boolean(addrErrors.street_address1)}
                    helperText={addrErrors.street_address1}
                    size="small"
                    InputProps={{
                      sx: { height: 40, minHeight: 40 }
                    }}
                    sx={{
                      '& .MuiInputBase-input': {
                        height: 40,
                        minHeight: 40,
                        boxSizing: 'border-box',
                        display: "flex",
                        alignItems: "center",
                        padding: '16.5px 14px'
                      }
                    }}
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label="Street Address 2"
                    value={addr.street_address2}
                    onChange={(e) => updateAddress(addr.id, 'street_address2', e.target.value)}
                    size="small"
                    InputProps={{
                      sx: { height: 40, minHeight: 40 }
                    }}
                    sx={{
                      '& .MuiInputBase-input': {
                        height: 40,
                        minHeight: 40,
                        boxSizing: 'border-box',
                        display: "flex",
                        alignItems: "center",
                        padding: '16.5px 14px'
                      }
                    }}
                  />
                </Grid>

                <Grid item xs={12} md={3}>
                  <TextField
                    fullWidth
                    label="City"
                    value={addr.city}
                    onChange={(e) => updateAddress(addr.id, 'city', e.target.value)}
                    error={Boolean(addrErrors.city)}
                    helperText={addrErrors.city}
                    size="small"
                    InputProps={{
                      sx: { height: 40, minHeight: 40 }
                    }}
                    sx={{
                      '& .MuiInputBase-input': {
                        height: 40,
                        minHeight: 40,
                        boxSizing: 'border-box',
                        display: "flex",
                        alignItems: "center",
                        padding: '16.5px 14px'
                      }
                    }}
                  />
                </Grid>
                <Grid item xs={12} md={3}>
                  <TextField
                    fullWidth
                    label="State"
                    value={addr.state}
                    onChange={(e) => updateAddress(addr.id, 'state', e.target.value)}
                    error={Boolean(addrErrors.state)}
                    helperText={addrErrors.state}
                    size="small"
                    InputProps={{
                      sx: { height: 40, minHeight: 40 }
                    }}
                    sx={{
                      '& .MuiInputBase-input': {
                        height: 40,
                        minHeight: 40,
                        boxSizing: 'border-box',
                        display: "flex",
                        alignItems: "center",
                        padding: '16.5px 14px'
                      }
                    }}
                  />
                </Grid>
                <Grid item xs={12} md={3}>
                  <TextField
                    fullWidth
                    label="Postal Code"
                    value={addr.postal_code}
                    onChange={(e) => updateAddress(addr.id, 'postal_code', e.target.value)}
                    error={Boolean(addrErrors.postal_code)}
                    helperText={addrErrors.postal_code}
                    size="small"
                    InputProps={{
                      sx: { height: 40, minHeight: 40 }
                    }}
                    sx={{
                      '& .MuiInputBase-input': {
                        height: 40,
                        minHeight: 40,
                        boxSizing: 'border-box',
                        display: "flex",
                        alignItems: "center",
                        padding: '16.5px 14px'
                      }
                    }}
                  />
                </Grid>
                <Grid item xs={12} md={3}>
                  <TextField
                    fullWidth
                    label="Country"
                    value={addr.country}
                    onChange={(e) => updateAddress(addr.id, 'country', e.target.value)}
                    size="small"
                    InputProps={{
                      sx: { height: 40, minHeight: 40 }
                    }}
                    sx={{
                      '& .MuiInputBase-input': {
                        height: 40,
                        minHeight: 40,
                        boxSizing: 'border-box',
                        display: "flex",
                        alignItems: "center",
                        padding: '16.5px 14px'
                      }
                    }}
                  />
                </Grid>

                {/* Latitude and Longitude Inputs */}
                <Grid item xs={12} md={4}>
                  <TextField
                    fullWidth
                    label="Latitude"
                    type="number"
                    value={addr.latitude || ''}
                    onChange={(e) => updateAddress(addr.id, 'latitude', e.target.value)}
                    error={Boolean(addrErrors.latitude)}
                    helperText={addrErrors.latitude}
                    size="small"
                    InputProps={{
                      sx: { height: 40, minHeight: 40 }
                    }}
                    sx={{
                      '& .MuiInputBase-input': {
                        height: 40,
                        minHeight: 40,
                        boxSizing: 'border-box',
                        display: "flex",
                        alignItems: "center",
                        padding: '16.5px 14px'
                      }
                    }}
                    required
                  />
                </Grid>
                <Grid item xs={12} md={4}>
                  <TextField
                    fullWidth
                    label="Longitude"
                    type="number"
                    value={addr.longitude || ''}
                    onChange={(e) => updateAddress(addr.id, 'longitude', e.target.value)}
                    error={Boolean(addrErrors.longitude)}
                    helperText={addrErrors.longitude}
                    size="small"
                    InputProps={{
                      sx: { height: 40, minHeight: 40 }
                    }}
                    sx={{
                      '& .MuiInputBase-input': {
                        height: 40,
                        minHeight: 40,
                        boxSizing: 'border-box',
                        display: "flex",
                        alignItems: "center",
                        padding: '16.5px 14px'
                      }
                    }}
                    required
                  />
                </Grid>
                <Grid item xs={12} md={4}>
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={Boolean(addr.is_default)}
                        onChange={(e) => updateAddress(addr.id, 'is_default', e.target.checked)}
                        color="primary"
                      />
                    }
                    label="Default address"
                  />
                </Grid>
                {/* End of Latitude and Longitude Inputs */}

              </Grid>
            </CardContent>
          </Card>
        )
      })}

      <MDButton variant="outlined" color="info" size="small" onClick={addAddress}>
        Add Address
      </MDButton>
    </MDBox>
  )
}

export default AddressesStep

